import Layout from "../Layout";
import React, { useEffect, useState } from 'react';
import {
    CircularProgress,
    Container,
    Grid,
    TextField,
    Button,
    Box,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Tooltip,
    Typography,
    IconButton,
    Select,
    InputLabel,
    FormControl,
    Chip,
    Card,
    CardContent,
    Zoom,
    Avatar,
} from '@mui/material';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';
import {
    FilterList,
    Search,
    Edit,
    AccountBalance,
    Assignment,
    TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from "../api";
import { useAuth } from "../context/userContext";
import TrackersModal from '../Components/TrackersModal.js';
import { toast } from "react-toastify";
import {
    isAdminRole,
    isLockedTrackerStatus,
} from '../utils/trackerStatusLock';
import { statusIn } from '../utils/trackerNormalization';

const theme = createTheme({
    palette: {
        primary: {
            main: '#05072E',
        },
        secondary: {
            main: '#6f74dd',
        },
        success: {
            main: '#66bb6a',
        },
        warning: {
            main: '#ffa726',
        },
        error: {
            main: '#e64a19',
        },
        background: {
            default: '#f3f4f6',
        },
    },
    typography: {
        fontSize: 13,
        h6: {
            fontWeight: 600,
            fontSize: '1rem',
        },
        body2: {
            fontSize: '0.85rem',
        },
    },
});

const StyledContainer = styled(Container)(({ theme }) => ({
    backgroundColor: '#ffffff',
    borderRadius: 20,
    padding: theme.spacing(3),
    boxShadow: '0 12px 30px rgba(15,23,42,0.12)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.6, 2.4),
    textTransform: 'none',
    borderRadius: '999px',
    fontSize: '0.8rem',
    transition: 'background-color 0.2s, transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 14px rgba(15,23,42,0.25)',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    margin: theme.spacing(0.5, 0),
    '& .MuiOutlinedInput-input': {
        padding: '8px',
        fontSize: '0.85rem',
    },
    '& label': {
        fontSize: '0.8rem',
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: '6px 12px',
    borderBottom: '1px solid #e5e7eb',
    '&:not(:last-child)': {
        borderRight: '1px solid #e5e7eb',
    },
    fontSize: '0.85rem',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    '& .MuiTableCell-head': {
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        color: theme.palette.common.white,
        fontWeight: 600,
        borderBottom: '1px solid #e5e7eb',
        '&:not(:last-child)': {
            borderRight: '1px solid #e5e7eb',
        },
        fontSize: '0.85rem',
        padding: '8px 12px',
        whiteSpace: 'nowrap',
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    minWidth: 230,
    margin: theme.spacing(1),
    padding: theme.spacing(1.5),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: theme.palette.common.white,
    boxShadow: '0px 10px 20px rgba(15,23,42,0.35)',
    borderRadius: '16px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0px 16px 30px rgba(15,23,42,0.55)',
    },
    '& .MuiCardContent-root': {
        padding: theme.spacing(1.5),
    },
    '& .MuiAvatar-root': {
        width: theme.spacing(5),
        height: theme.spacing(5),
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    },
}));

const Trackers = () => {
    const [selections, setSelections] = useState({
        company: '',
        location: [],
        lawArea: [],
        actRule: [],
        periodicity: [],
        status: [],
        startDate: '',
        endDate: '',
    });
    const [companies, setCompanies] = useState([]);
    const [locations, setLocations] = useState([]);
    const [lawAreas, setLawAreas] = useState([]);
    const [options, setOptions] = useState({
        companies: [],
        locations: [],
        lawAreas: [],
        actRule: '',
        status: [
            { label: 'Open', value: 'open' },
            { label: 'Pending for Review', value: 'pending for review' },
            { label: 'Pending for Discrepancy', value: 'pending for discrepancy' },
            { label: 'Delayed Compliance', value: 'delayed' },
            { label: 'Not Applicable', value: 'na' },
            { label: 'Complied', value: 'complied' },
        ],
        periodicities: [
            'Annual', 'Weekly', 'Event based', 'One time', 'All time',
            'Half-yearly', 'Monthly', 'Quarterly',
            'Fortnightly', 'Daily', 'Bi-Weekly',
        ],
        searchFields: [
            'UIN', 'Location', 'Compliance Activity', 'Law Area',
            'Act or Rule', 'Risk', 'Form', 'Consequence', 'Status',
        ],
    });
    const [state] = useAuth();
    const [reportData, setReportData] = useState({
        totalTrackers: 0,
        pendingReview: 0,
        complied: 0,
    });

    const [dashboard, setDashboard] = useState(null);
    const [loadingDash, setLoadingDash] = useState(true);
    const [modalTitle, setModalTitle] = useState('');
    const [modalRows, setModalRows] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchField, setSearchField] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const navigate = useNavigate();
    const isAdmin = isAdminRole(state?.user?.role);

    const searchFieldMap = {
        UIN: 'uniqueIdentifier',
        Location: 'location',
        'Compliance Activity': 'complianceActivity',
        'Law Area': 'lawArea',
        'Act or Rule': 'actOrRule',
        Risk: 'risk',
        Form: 'form',
        Consequence: 'consequence',
        Status: 'status',
    };

    const getRowsFor = (type) => {
        if (!data.length) return [];
        switch (type) {
            case 'pending':
                return data.filter((t) => statusIn(t.status, [
                    'open',
                    'pending for review',
                    'pending for discrepancy',
                    'in-process',
                    'cannot-comply',
                    'not-complied',
                ]));
            case 'completed':
                return data.filter((t) => statusIn(t.status, [
                    'complied',
                    'complied on time',
                    'delayed',
                    'delayed compliance',
                    'delayed complied',
                    'not applicable',
                ]));
            default:
                return data;
        }
    };

    const openModal = (kind) => {
        if (!dashboard) return;
        setModalTitle(
            kind === 'pending'
                ? 'Pending Trackers'
                : kind === 'completed'
                    ? 'Completed Trackers'
                    : 'All Trackers'
        );
        setModalRows(
            dashboard.lists[
            kind === 'pending'
                ? 'pending'
                : kind === 'completed'
                    ? 'complied'
                    : 'all'
            ]
        );
        setModalOpen(true);
    };
    const closeModal = () => setModalOpen(false);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const { data } = await api.get('tracker/trackers-summary');
                setDashboard(data);
                setReportData({
                    totalTrackers: data.totals.all,
                    pendingReview: data.totals.pending,
                    complied: data.totals.complied,
                });
            } catch (err) {
                console.error('summary fetch err', err);
            } finally {
                setLoadingDash(false);
            }
        };
        fetchSummary();
    }, []);

    useEffect(() => {
        const fetchTrackerDetails = async () => {
            try {
                const { data } = await api.post(
                    "reports/get-tracker-details-all-trackers",
                    { user: state?.name }
                );
                const { totalTrackers, pendingReview, complied } = data;
                setReportData({ totalTrackers, pendingReview, complied });
            } catch (error) {
                console.error("Error fetching tracker details:", error);
            }
        };

        fetchTrackerDetails();
    }, [state?.name]);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const { data } = await api.get('detail/userinfo?view=meta');
                const companyNames = Array.isArray(data.companyDetails.company_name)
                    ? data.companyDetails.company_name
                    : data.companyDetails.company_name
                        ? [data.companyDetails.company_name]
                        : [];
                setCompanies(companyNames);
                setLocations(data.companyDetails.locations || {});
                setOptions((prevOptions) => ({
                    ...prevOptions,
                    companies: companyNames,
                    locations: data.companyDetails.locations.flat() || [],
                }));
            } catch (error) {
                console.error("Error fetching companies data:", error);
            }
        };

        const fetchLawAreas = async () => {
            try {
                const response = await api.get('tracker/law-area');
                if (response.data && response.data.lawareas) {
                    const lawAreaArray = response.data.lawareas.map(
                        (lawArea) => lawArea.law_area
                    );
                    setLawAreas(lawAreaArray);
                    setOptions((prevOptions) => ({
                        ...prevOptions,
                        lawAreas: lawAreaArray,
                    }));
                } else {
                    console.error("Unexpected response structure:", response.data);
                }
            } catch (error) {
                console.error("Error fetching law areas data:", error);
            }
        };

        fetchLawAreas();
        fetchCompanies();
    }, []);

    const handleSelectionChange = (field) => (event) => {
        setSelections({ ...selections, [field]: event.target.value });
    };

    const handleSearchFieldChange = (event) => {
        setSearchField(event.target.value);
    };

    const handleSearchValueChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post(
                `tracker/get-trackers`,
                selections,
                { withCredentials: true }
            );
            setData(data);
            setFilteredData(data);
        } catch (error) {
            toast.error('API request error:', error);
        }
    };

    const handleSearch = () => {
        if (searchField && searchValue) {
            const actualKey = searchFieldMap[searchField];
            if (!actualKey) {
                setFilteredData(data);
                return;
            }
            const filtered = data.filter((row) => {
                const fieldValue = row[actualKey];
                return (
                    fieldValue &&
                    fieldValue.toString().toLowerCase().includes(searchValue.toLowerCase())
                );
            });
            setFilteredData(filtered);
        } else {
            setFilteredData(data);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleClear = () => {
        setSelections({
            company: '',
            location: [],
            lawArea: [],
            actRule: [],
            periodicity: [],
            status: [],
            startDate: '',
            endDate: '',
        });
        setData([]);
        setFilteredData([]);
    };

    const handleEdit = async (uin) => {
        navigate(`/Edit/${uin}`);
    };
    const canEditTracker = (status) => isAdmin || !isLockedTrackerStatus(status);

    const ALL = 'All';

    const handleLocationChange = (event) => {
        const value = event.target.value;
        if (value.includes(ALL)) {
            setSelections((prev) => ({ ...prev, location: options.locations }));
        } else {
            setSelections((prev) => ({ ...prev, location: value }));
        }
    };

    const handleStatusChange = (event) => {
        const value = event.target.value;
        if (value.includes(ALL)) {
            const allStatuses = options.status.map((s) => s.value);
            setSelections((prev) => ({ ...prev, status: allStatuses }));
        } else {
            setSelections((prev) => ({ ...prev, status: value }));
        }
    };

    return (
        <Layout title="All Trackers">
            <ThemeProvider theme={theme}>
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 3 }}>
                    <StyledContainer maxWidth="xl">
                        {/* Header */}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                        >
                            <Box display="flex" alignItems="center">
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    All Trackers
                                </Typography>
                                <Tooltip title="Filter List" arrow>
                                    <FilterList
                                        sx={{
                                            ml: 1.5,
                                            color: theme.palette.primary.main,
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Search, filter, and review all compliance trackers in one place.
                            </Typography>
                        </Box>

                        {/* Summary cards */}
                        {loadingDash ? (
                            <Box display="flex" justifyContent="center" my={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={1}>
                                <Zoom in timeout={300}>
                                    <Grid item xs={12} sm={4}>
                                        <StyledCard onClick={() => openModal('all')}>
                                            <CardContent>
                                                <Box display="flex" alignItems="center">
                                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}>
                                                        <AccountBalance fontSize="small" />
                                                    </Avatar>
                                                    <Box ml={1.5}>
                                                        <Typography variant="h6">Total Trackers</Typography>
                                                        <Typography variant="body2">
                                                            {reportData.totalTrackers} Trackers
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>
                                </Zoom>

                                <Zoom in timeout={350}>
                                    <Grid item xs={12} sm={4}>
                                        <StyledCard onClick={() => openModal('pending')}>
                                            <CardContent>
                                                <Box display="flex" alignItems="center">
                                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}>
                                                        <Assignment fontSize="small" />
                                                    </Avatar>
                                                    <Box ml={1.5}>
                                                        <Typography variant="h6">Pending Reviews</Typography>
                                                        <Typography variant="body2">
                                                            {reportData.pendingReview} Pending
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>
                                </Zoom>

                                <Zoom in timeout={400}>
                                    <Grid item xs={12} sm={4}>
                                        <StyledCard onClick={() => openModal('completed')}>
                                            <CardContent>
                                                <Box display="flex" alignItems="center">
                                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}>
                                                        <TrendingUp fontSize="small" />
                                                    </Avatar>
                                                    <Box ml={1.5}>
                                                        <Typography variant="h6">Completed Trackers</Typography>
                                                        <Typography variant="body2">
                                                            {reportData.complied} Completed
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </StyledCard>
                                    </Grid>
                                </Zoom>
                            </Grid>
                        )}

                        {/* Filters */}
                        <form onSubmit={handleSubmit}>
                            <Box
                                mt={3}
                                mb={1}
                                p={2}
                                borderRadius={2}
                                sx={{ bgcolor: 'grey.50', border: '1px solid #e5e7eb' }}
                            >
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <StyledTextField
                                            label="Company"
                                            variant="outlined"
                                            select
                                            value={selections.company}
                                            onChange={handleSelectionChange('company')}
                                            fullWidth
                                            size="small"
                                        >
                                            {options.companies.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </StyledTextField>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                                            <InputLabel sx={{ fontSize: '0.8rem' }}>Location</InputLabel>
                                            <Select
                                                label="Location"
                                                variant="outlined"
                                                multiple
                                                value={selections.location}
                                                onChange={handleLocationChange}
                                                renderValue={(selected) => (
                                                    <Box
                                                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                                                    >
                                                        {selected.map((value) => (
                                                            <Chip
                                                                key={value}
                                                                label={value}
                                                                sx={{ height: '20px', fontSize: '0.75rem' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            >
                                                <MenuItem value={ALL}>
                                                    <em>All</em>
                                                </MenuItem>
                                                {options.locations.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                                            <InputLabel sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
                                            <Select
                                                label="Status"
                                                variant="outlined"
                                                multiple
                                                value={selections.status}
                                                onChange={handleStatusChange}
                                                renderValue={(selected) => (
                                                    <Box
                                                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                                                    >
                                                        {selected.map((value) => {
                                                            const item = options.status.find(
                                                                (s) => s.value === value
                                                            );
                                                            return (
                                                                <Chip
                                                                    key={value}
                                                                    label={item ? item.label : value}
                                                                    sx={{ height: '20px', fontSize: '0.75rem' }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            >
                                                <MenuItem value={ALL}>
                                                    <em>All</em>
                                                </MenuItem>
                                                {options.status.map(({ value, label }) => (
                                                    <MenuItem key={value} value={value}>
                                                        {label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <StyledTextField
                                            label="Act / Rule"
                                            variant="outlined"
                                            value={selections.actRule}
                                            onChange={handleSelectionChange('actRule')}
                                            placeholder="Enter Act or Rule"
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                                            <InputLabel sx={{ fontSize: '0.8rem' }}>
                                                Periodicity
                                            </InputLabel>
                                            <Select
                                                label="Periodicity"
                                                variant="outlined"
                                                multiple
                                                value={selections.periodicity}
                                                onChange={handleSelectionChange('periodicity')}
                                                renderValue={(selected) => (
                                                    <Box
                                                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                                                    >
                                                        {selected.map((value) => (
                                                            <Chip
                                                                key={value}
                                                                label={value}
                                                                sx={{ height: '20px', fontSize: '0.75rem' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            >
                                                {options.periodicities.map((p) => (
                                                    <MenuItem key={p} value={p}>
                                                        {p}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <StyledTextField
                                            label="Start Due Date"
                                            type="date"
                                            variant="outlined"
                                            value={selections.startDate}
                                            onChange={handleSelectionChange('startDate')}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <StyledTextField
                                            label="End Due Date"
                                            type="date"
                                            variant="outlined"
                                            value={selections.endDate}
                                            onChange={handleSelectionChange('endDate')}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            size="small"
                                        />
                                    </Grid>

                                    <Grid item xs={12} mt={1}>
                                        <Box display="flex" justifyContent="flex-end">
                                            <StyledButton
                                                variant="contained"
                                                color="primary"
                                                type="submit"
                                                startIcon={<Search fontSize="small" />}
                                            >
                                                Apply
                                            </StyledButton>
                                            <StyledButton
                                                variant="outlined"
                                                color="primary"
                                                onClick={handleClear}
                                            >
                                                Clear
                                            </StyledButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </form>

                        {/* Search bar */}
                        <Box
                            display="flex"
                            alignItems="center"
                            mt={1}
                            mb={1}
                            sx={{ gap: 1 }}
                        >
                            <FormControl sx={{ minWidth: 150 }} size="small">
                                <InputLabel sx={{ fontSize: '0.8rem' }}>Search Field</InputLabel>
                                <Select
                                    value={searchField}
                                    onChange={handleSearchFieldChange}
                                    label="Search Field"
                                >
                                    {options.searchFields.map((field) => (
                                        <MenuItem key={field} value={field}>
                                            {field}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Search Value"
                                variant="outlined"
                                value={searchValue}
                                onChange={handleSearchValueChange}
                                size="small"
                                sx={{ flex: 1 }}
                                InputProps={{ style: { fontSize: '0.85rem' } }}
                                InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearch}
                                startIcon={<Search fontSize="small" />}
                                sx={{
                                    transition: 'transform 0.2s',
                                    borderRadius: '999px',
                                    fontSize: '0.8rem',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                Search
                            </Button>
                        </Box>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            Showing {filteredData.length} result
                            {filteredData.length !== 1 ? 's' : ''}
                        </Typography>

                        {/* Table */}
                        <Zoom in timeout={300}>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    mt: 1,
                                    mb: 1,
                                    borderRadius: 2,
                                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                }}
                            >

                                <Table size="small" stickyHeader>
                                    <StyledTableHead>
                                        <TableRow>
                                            <StyledTableCell>UIN</StyledTableCell>
                                            <StyledTableCell>Location</StyledTableCell>
                                            <StyledTableCell>Compliance Activity</StyledTableCell>
                                            <StyledTableCell>Law Area</StyledTableCell>
                                            <StyledTableCell>Act or Rule</StyledTableCell>
                                            <StyledTableCell>Due Date</StyledTableCell>
                                            <StyledTableCell>Risk</StyledTableCell>
                                            <StyledTableCell>Form</StyledTableCell>
                                            <StyledTableCell>Consequence</StyledTableCell>
                                            <StyledTableCell>Proof</StyledTableCell>
                                            <StyledTableCell>Status</StyledTableCell>
                                            <StyledTableCell>Details</StyledTableCell>
                                        </TableRow>
                                    </StyledTableHead>
                                    <TableBody>
                                        {filteredData != null &&
                                            filteredData
                                                .slice(
                                                    page * rowsPerPage,
                                                    page * rowsPerPage + rowsPerPage
                                                )
                                                .map((row) => (
                                                    <TableRow
                                                        key={row.uniqueIdentifier}
                                                        hover
                                                        sx={{
                                                            '&:nth-of-type(even)': {
                                                                backgroundColor: '#f9fafb',
                                                            },
                                                        }}
                                                    >
                                                        <StyledTableCell>{row.uniqueIdentifier}</StyledTableCell>
                                                        <StyledTableCell>{row.location}</StyledTableCell>
                                                        <StyledTableCell>
                                                            {row.complianceActivity}
                                                        </StyledTableCell>
                                                        <StyledTableCell>{row.lawArea}</StyledTableCell>
                                                        <StyledTableCell>{row.actOrRule}</StyledTableCell>
                                                        <StyledTableCell>
                                                            {new Date(row.dueDate).toLocaleDateString()}
                                                        </StyledTableCell>
                                                        <StyledTableCell>{row.risk}</StyledTableCell>
                                                        <StyledTableCell>{row.form}</StyledTableCell>
                                                        <StyledTableCell>{row.consequence}</StyledTableCell>
                                                        <StyledTableCell>
                                                            {row.proof ? 'Yes' : 'No'}
                                                        </StyledTableCell>
                                                        <StyledTableCell>{row.status}</StyledTableCell>
                                                        <StyledTableCell>
                                                            {canEditTracker(row.status) ? (
                                                                <Tooltip title="Edit Detail" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            const UIN = JSON.stringify(
                                                                                row.uniqueIdentifier
                                                                            ).replace(/"/g, '');
                                                                            handleEdit(UIN);
                                                                        }}
                                                                    >
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Admin only
                                                                </Typography>
                                                            )}
                                                        </StyledTableCell>
                                                    </TableRow>
                                                ))}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={filteredData.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    sx={{ fontSize: '0.8rem' }}
                                />
                            </TableContainer>
                        </Zoom>
                    </StyledContainer>
                </Box>
            </ThemeProvider>

            {/* Modal for summary cards */}
            <TrackersModal
                open={modalOpen}
                title={modalTitle}
                rows={modalRows}
                onClose={closeModal}
                onEdit={handleEdit}
                isAdmin={isAdmin}
            />
        </Layout>
    );
};

export default Trackers;
